import { useCallback, useContext, useEffect, useMemo, useRef, useState } from "react"
import { Button, Divider, Input, Modal, message } from "antd";
import Iconfont from '@/utils/Iconfont';
import styles from './index.module.scss'
import VideoLabels from "../VideoLabels";
import Utils from '@/utils/utils';
import PSAIModal from "@PSAIComponents/PSAIModal";
import ReactCrop from 'react-image-crop';
import 'react-image-crop/dist/ReactCrop.css';
import { PictureViewerContext } from "../../hooks/PictureViewerContext"
import eventBus from '@/utils/eventBus';

export default function LabelToolBox(props) {
    const {
        hasNext
    } = useContext(PictureViewerContext)

    const [mode, setMode] = useState() // rect/ellipse/brush/arrow/text
    const [activePanel, setActivePanel] = useState(null)
    const [commentValue, setCommentValue] = useState(props.commentValue || '')
    const imageUploadRef = useRef()
    // 上传图裁图
    const [cropModalOpen, setCropModalOpen] = useState(false)
    const [cropSource, setCropSource] = useState(null)
    const [completedCrop, setCompletedCrop] = useState(null)
    const [cropLoading, setCropLoading] = useState(false)
    const [imageWidth, setImageWidth] = useState(0)
    const [imageHeight, setImageHeight] = useState(0)
    const isMountedRef = useRef(true)

    const isVideo = !!props.isVideo
    const isReadonly = !!props.readonly
    const showCommentTrigger = !isReadonly || !!props.commentValue
    const showFrameTrigger = isVideo && (!isReadonly || (props.frameLabels || []).length > 0)
    const undoDisabled = useMemo(() => {
        return props.canvasAnnotations?.length === 0
    }, [props.canvasAnnotations])
    const clearDisabled = useMemo(() => {
        return props.canvasAnnotations?.length === 0
    }, [props.canvasAnnotations])

    useEffect(() => {
        props.onChange(mode)
    }, [mode, props.onChange])

    useEffect(() => {
        setCommentValue(props.commentValue || '')
    }, [props.commentValue])

    useEffect(() => {
        return () => {
            isMountedRef.current = false
        }
    }, [])

    const handleModeChange = useCallback((value) => {
        if (isReadonly) return
        setActivePanel(null)
        setMode(pre => {
            if (pre != value) return value
            else return null
        })
    }, [isReadonly])

    const handleCommentModeChange = useCallback(() => {
        setMode(null)
        setActivePanel(pre => pre === 'comment' ? null : 'comment')
    }, [])

    const handleFrameCommentModeChange = useCallback(() => {
        setMode(null)
        setActivePanel(pre => pre === 'frame' ? null : 'frame')
    }, [])

    const handleImageModeChange = useCallback(() => {
        if (isReadonly) return
        setActivePanel(null)
        setMode('image')
        window.requestAnimationFrame(() => {
            imageUploadRef.current?.click()
        })
    }, [isReadonly])

    // Confirm操作
    const handleConfirm = useCallback(() => {
        if (isReadonly) return
        if (props.onConfirm) {
            props.onConfirm()
            return
        }
    }, [isReadonly, props.onConfirm])

    const handleImageUploadChange = useCallback(async (e) => {
        const file = e.target.files?.[0]
        e.target.value = ''
        if (!file) return
        try {
            const isNromal = checkImageIsNormal(file)
            if (!isNromal) return
            setCropSource(file)
            setCompletedCrop(null)
            setCropModalOpen(true)
        } catch (err) {
            console.error(err)
            message.warning(err.message)
        }
    }, [])

    const handleCropComplete = useCallback((percentCrop, width, height) => {
        setCompletedCrop(percentCrop)
        setImageWidth(width)
        setImageHeight(height)
    }, [])

    const handleCropCancel = useCallback(() => {
        setCropModalOpen(false)
        setCropSource(null)
        setCompletedCrop(null)
        setCropLoading(false)
    }, [])

    const handleCropConfirm = useCallback(async () => {
        if (!cropSource || !completedCrop || !imageWidth || !imageHeight) {
            message.warning('请选择要上传的图片区域')
            return
        }
        try {
            setCropLoading(true)
            const type = cropSource.type
            // 裬换为像素坐标
            completedCrop.x = completedCrop.x * imageWidth / 100
            completedCrop.y = completedCrop.y * imageHeight / 100
            completedCrop.width = completedCrop.width * imageWidth / 100
            completedCrop.height = completedCrop.height * imageHeight / 100

            const croppedBase64 = await cropImageBase64(
                {
                    src: URL.createObjectURL(cropSource),
                    x: Math.round(completedCrop.x),
                    y: Math.round(completedCrop.y),
                    width: Math.round(completedCrop.width),
                    height: Math.round(completedCrop.height),
                },
                type
            )
            const file = Utils.convertBase64ToFile(croppedBase64, type === 'image/png' ? 'image.png' : 'image.jpg')
            const url = await Utils.uploadFile(file)
            if (!url) return
            await props.onImageUpload?.(url)
            if (!isMountedRef.current) return
            handleCropCancel()
        } catch (err) {
            console.error(err)
            if (isMountedRef.current) {
                message.warning(err.message || '图片裁剪上传失败')
            }
        } finally {
            if (isMountedRef.current) {
                setCropLoading(false)
            }
        }
    }, [
        cropSource, completedCrop, imageWidth, imageHeight,
        props.onImageUpload, handleCropCancel
    ])

    const handleCommentSave = () => {
        if (isReadonly) return
        props.onCommentSave?.(commentValue.trim())
        setActivePanel(null)
    }

    // 将工具栏工具通过toolItems管理渲染
    const toolItems = useMemo(() => {
        const showCanvasTools = !isVideo && !isReadonly && !props.canvasToolsDisabled
        return [
            {
                key: 'undo',
                name: '撤销',
                icon: <Iconfont className={styles.ToolIcon} type="icon-tool-undo" />,
                visible: showCanvasTools,
                actived: false,
                disabled: undoDisabled,
                onClick: props.onUndo,
            },
            {
                key: 'clear',
                name: '清空',
                icon: <Iconfont className={styles.ToolIcon} type="icon-tool-delete" />,
                visible: showCanvasTools,
                actived: false,
                disabled: clearDisabled,
                onClick: props.onReset,
            },
            {
                key: 'divider',
                type: 'divider',
                visible: showCanvasTools,
            },
            {
                key: 'rect',
                name: '矩形',
                icon: <Iconfont className={styles.ToolIcon} type="icon-tool-suare" />,
                visible: showCanvasTools,
                actived: mode === 'rect',
                onClick: () => handleModeChange('rect'),
            },
            {
                key: 'ellipse',
                name: '圆形',
                icon: <Iconfont className={styles.ToolIcon} type="icon-tool-circle" />,
                visible: showCanvasTools,
                actived: mode === 'ellipse',
                onClick: () => handleModeChange('ellipse'),
            },
            {
                key: 'brush',
                name: '画笔',
                icon: <Iconfont className={styles.ToolIcon} type="icon-tool-pencil" />,
                visible: showCanvasTools,
                actived: mode === 'brush',
                onClick: () => handleModeChange('brush'),
            },
            {
                key: 'arrow',
                name: '箭头',
                icon: <Iconfont className={styles.ToolIcon} type="icon-tool-arrow" />,
                visible: showCanvasTools,
                actived: mode === 'arrow',
                onClick: () => handleModeChange('arrow'),
            },
            {
                key: 'text',
                name: '文本',
                icon: <Iconfont className={styles.ToolIcon} type="icon-tool-text" />,
                visible: showCanvasTools,
                actived: mode === 'text',
                onClick: () => handleModeChange('text'),
            },
            {
                key: 'image',
                name: '贴图',
                icon: <Iconfont className={styles.ToolIcon} type="icon-tool-pic" />,
                visible: showCanvasTools,
                actived: mode === 'image',
                onClick: handleImageModeChange,
            },
            {
                key: 'comment',
                name: '修改意见',
                icon: <Iconfont className={styles.ToolIcon} type="icon-tool-comment" />,
                visible: showCommentTrigger,
                actived: activePanel === 'comment',
                onClick: handleCommentModeChange,
            },
            {
                key: 'frame',
                name: '按帧截图评论',
                icon: <Iconfont className={styles.ToolIcon} type="icon-tool-frame" />,
                visible: showFrameTrigger,
                actived: activePanel === 'frame',
                onClick: handleFrameCommentModeChange,
            },
            {
                key: 'confirm',
                name: props.confirmText || (hasNext ? '保存，下一张' : '保存并返回'),
                icon: <Iconfont className={styles.ToolIcon} type="icon-tool-confirm" style={{ color: '#43C287' }} />,
                visible: !isReadonly,
                actived: false,
                onClick: handleConfirm,
            },
        ].filter(item => item.visible)
    }, [
        hasNext,
        mode, activePanel,
        isVideo, isReadonly, showCommentTrigger, showFrameTrigger,
        undoDisabled, clearDisabled,
        handleModeChange, handleCommentModeChange, handleFrameCommentModeChange, handleImageModeChange,
        props.onReset, props.onUndo, props.canvasToolsDisabled,
        handleConfirm, props.confirmText,
    ])

    return (
        <div className={styles.LabelToolBoxWrap}>
            <input
                ref={imageUploadRef}
                type="file"
                accept="image/*"
                style={{ display: 'none' }}
                onChange={handleImageUploadChange}
            />
            <PSAIModal
                title="上传贴图"
                open={cropModalOpen}
                maxWidth={1008}
                // width={1080}
                centered
                destroyOnClose
                getContainer={() => document.body}
                confirmLoading={cropLoading}
                okText="上传"
                onCancel={handleCropCancel}
                onOk={handleCropConfirm}
            >
                <ImageCrop
                    cropSource={cropSource}
                    onComplete={handleCropComplete}
                />
            </PSAIModal>
            {activePanel === 'comment' ?
                <div className={styles.CommentEditor}>
                    <Input.TextArea
                        value={commentValue}
                        disabled={isReadonly}
                        placeholder="请详细描述您的修改意见..."
                        variant="borderless"
                        autoSize={{ minRows: 5, maxRows: 5 }}
                        onChange={e => setCommentValue(e.target.value)}
                    />
                    <div>
                        <div className={styles.CommentTips}>选择【不通过】时，必须进行标注或填写修改意见</div>
                        {isReadonly ? null :
                            <Button type="primary" onClick={handleCommentSave}>保存</Button>}
                    </div>
                </div> : null}
            {activePanel === 'frame' ?
                <div className={styles.FrameCommentEditor}>
                    <VideoLabels
                        src={props.src}
                        readonly={props.readonly}
                        labels={props.frameLabels || []}
                        onLabel={props.onFrameLabelsChange}
                    />
                </div> : null}
            <div className={`${styles.ModeRatio} ${isVideo ? styles.VideoModeRatio : ''}`}>
                {toolItems.map(item => (
                    item.key == "divider" ?
                        <Divider
                            key={item.key}
                            type="vertical"
                            style={{
                                borderColor: "rgba(34, 55, 244, 0.3)",
                                height: 20,
                                marginInline: 0
                            }}
                        /> :
                        <div
                            key={item.key}
                            title={item.name}
                            className={`${styles.ToolItem} ${item.actived ? styles.actived : ''} ${item.disabled ? styles.disabled : ''}`}
                            onClick={item.disabled ? null : item.onClick}
                        >
                            {item.icon}
                        </div>
                ))}
            </div>
        </div>
    )
}


function ImageCrop(props) {
    const { cropSource, onComplete } = props
    const [imageWidth, setImageWidth] = useState(0)
    const [imageHeight, setImageHeight] = useState(0)
    const [crop, setCrop] = useState({
        unit: '%',
        x: 0,
        y: 0,
        width: 100,
        height: 100,
    })
    const onImageLoad = useCallback((e) => {
        setImageWidth(e.target.naturalWidth)
        setImageHeight(e.target.naturalHeight)
    }, [])
    useEffect(() => {
        if (!crop) return
        onComplete(crop, imageWidth, imageHeight)
    }, [crop, imageWidth, imageHeight])
    const src = useMemo(() => {
        return URL.createObjectURL(cropSource)
    }, [cropSource])

    if (!src) return null

    return (
        <div className={styles.CropModalBody}>
            <ReactCrop
                crop={crop}
                keepSelection
                onChange={(_, percentCrop) => setCrop(percentCrop)}
                onComplete={(_, percentCrop) => onComplete(percentCrop, imageWidth, imageHeight)}
            >
                <img
                    src={src}
                    alt="crop-source"
                    onLoad={onImageLoad}
                />
            </ReactCrop>
        </div>
    )
}

// 检查图片，50M限制，不再限制图片尺寸不小于500*500，长宽比不超过2:1
function checkImageIsNormal(file) {
    const MAX_IMAGE_SIZE = 50 * 1024 * 1024
    try {
        const isJpgOrPng = file.type === 'image/jpeg' || file.type === 'image/png' || file.type === 'image/webp' || file.type === 'image/avif';
        if (!isJpgOrPng) throw new Error('您只能上传JPG/PNG/WEBP/AVIF格式的文件')
        if (file.size > MAX_IMAGE_SIZE) throw new Error(`图片最大支持${MAX_IMAGE_SIZE / 1024 / 1024}MB，请压缩体积后上传`)
        return true
    } catch (err) {
        message.warning(err.message)
        return false
    }
}

function cropImageBase64({src, x, y, width, height}, type="image/png") {
    return new Promise((resolve, reject) => {
        const MAX_OUTPUT_SIZE = 8192
        const image = new Image();
        image.crossOrigin = "anonymous"
        image.onload = function () {
            const scale = Math.min(1, MAX_OUTPUT_SIZE / Math.max(width, height))
            const outputWidth = Math.max(1, Math.round(width * scale))
            const outputHeight = Math.max(1, Math.round(height * scale))
            const canvas = document.createElement('canvas');
            const context = canvas.getContext('2d');
            canvas.width = outputWidth;
            canvas.height = outputHeight;
            context.drawImage(image, x, y, width, height, 0, 0, outputWidth, outputHeight);
            const croppedBase64 = canvas.toDataURL(type, 1);
            resolve(croppedBase64);
        };
        image.onerror = function (error) {
            reject(error);
        };
        image.src = src;
    });
}
